import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { scrollToSection } from "@/components/Reveal";

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("supporter");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/newsletter", { email, role });
      toast.success("You're on the roster!", { description: "Watch your inbox for GAME updates." });
      setEmail("");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer className="border-t border-[#242436] bg-[#0C0C12]" data-testid="site-footer">
      <div className="px-4 sm:px-8 lg:px-16 py-16 grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div>
          <div className="flex items-center gap-4">
            <img src="/logo.webp" alt="G.A.M.E. Mentorship and Training Inc. logo" className="h-20 w-auto rounded-sm ring-1 ring-[#FFB800]/60" />
            <div>
              <div className="font-heading font-black text-lg text-[#F4F4F6] leading-tight">G.A.M.E.</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A1A1B3]">Mentorship & Training Inc.</div>
            </div>
          </div>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#62627A] mt-3 max-w-sm leading-relaxed">
            Grind. Achieve. Motivate. Excel. Founded by Jeffery Mizell.
          </p>
          <div className="mt-4 space-y-1.5">
            <a href="mailto:jmizell0057@gmail.com" data-testid="footer-email-link" className="block font-mono text-xs tracking-[0.15em] text-[#A1A1B3] hover:text-[#FFB800] transition-colors">jmizell0057@gmail.com</a>
            <a href="tel:+14782313286" data-testid="footer-phone-link" className="block font-mono text-xs tracking-[0.15em] text-[#A1A1B3] hover:text-[#FFB800] transition-colors">(478) 231-3286</a>
          </div>
          <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.15em] text-[#62627A] max-w-sm leading-relaxed">
            Donations support G.A.M.E. Mentorship and Training Inc. (nonprofit). Commercial training and campus operations are run by G.A.M.E. Training LLC.
          </p>
          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2">
            {["story", "nonprofit", "impact", "campus", "donate"].map((id) => (
              <button
                key={id}
                onClick={() => scrollToSection(`#${id}`)}
                data-testid={`footer-${id}-link`}
                className="font-mono text-xs uppercase tracking-[0.2em] text-[#A1A1B3] hover:text-[#8B5CF6] transition-colors duration-200"
              >
                {id}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="font-display font-bold uppercase tracking-wide text-xl text-[#F4F4F6]">Get the playbook in your inbox</div>
          <form onSubmit={subscribe} className="mt-5 flex flex-col sm:flex-row gap-3" data-testid="newsletter-form">
            <input
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              data-testid="newsletter-email-input"
              className="flex-1 bg-[#161622] border border-[#242436] focus:border-[#8B5CF6] outline-none text-[#F4F4F6] text-sm px-4 py-3.5 placeholder:text-[#62627A] transition-colors duration-200"
            />
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              data-testid="newsletter-role-select"
              className="bg-[#161622] border border-[#242436] focus:border-[#8B5CF6] outline-none text-[#A1A1B3] text-sm px-4 py-3.5 transition-colors duration-200"
            >
              <option value="supporter">Supporter</option>
              <option value="parent">Parent</option>
              <option value="coach">Coach</option>
              <option value="sponsor">Sponsor</option>
            </select>
            <button
              type="submit"
              disabled={busy}
              data-testid="newsletter-form-submit"
              className="bg-[#8B5CF6] text-black font-display font-extrabold uppercase tracking-wider text-sm px-6 py-3.5 flex items-center justify-center gap-2 hover:bg-[#FFB800] active:scale-95 disabled:opacity-50 transition-all duration-200"
            >
              {busy ? "..." : "Join"}
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        </div>
      </div>
      <div className="border-t border-[#242436] px-4 sm:px-8 lg:px-16 py-6 flex flex-col sm:flex-row justify-between gap-2">
        <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#62627A]">© 2026 G.A.M.E. Mentorship and Training Inc. All rights reserved.</span>
        <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#62627A]">A 501(c)(3) tax-exempt nonprofit organization.</span>
      </div>
    </footer>
  );
};
